import { supabase } from '@/integrations/supabase/client';
import { enableAstrologyForUser } from '../hooks/useAstrologyAccess';
import { computeChart, chartFromPositions } from '../engine/chart';
import type { HouseSystem } from '../engine/houses';

export const ASTROLOGY_PREVIEW_KEY = 'psyforge:astrology_preview';

export type AstrologyPreviewPayload =
  | {
      kind: 'birth';
      data: {
        name?: string;
        year: number; month: number; day: number;
        hour: number; minute: number;
        tzOffset: number; lat: number; lon: number;
        placeName?: string;
        ianaTz?: string;
        houseSystem?: HouseSystem;
      };
      savedAt: number;
    }
  | {
      kind: 'imported';
      positions: Record<string, number>;
      houseSystem?: HouseSystem;
      savedAt: number;
    };

export function readAstrologyPreview(): AstrologyPreviewPayload | null {
  try {
    const raw = localStorage.getItem(ASTROLOGY_PREVIEW_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AstrologyPreviewPayload;
    if (!parsed || (parsed.kind !== 'birth' && parsed.kind !== 'imported')) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeAstrologyPreview(payload: AstrologyPreviewPayload) {
  try {
    localStorage.setItem(ASTROLOGY_PREVIEW_KEY, JSON.stringify(payload));
  } catch {
    /* noop */
  }
}

export function clearAstrologyPreview() {
  try {
    localStorage.removeItem(ASTROLOGY_PREVIEW_KEY);
  } catch {
    /* noop */
  }
}

/**
 * One-shot migration: if a guest cast a preview chart in localStorage,
 * persist it to birth_charts + astrology_chart_cache and enable the feature
 * for the freshly authenticated user. Silent no-op when nothing to migrate.
 */
export async function migrateAstrologyPreview(userId: string): Promise<boolean> {
  const preview = readAstrologyPreview();
  if (!preview) return false;

  try {
    await enableAstrologyForUser(userId);

    if (preview.kind === 'birth') {
      const d = preview.data;
      const payload = {
        user_id: userId,
        label: d.name || 'self',
        birth_year: d.year,
        birth_month: d.month,
        birth_day: d.day,
        birth_hour: d.hour,
        birth_minute: d.minute,
        tz_offset: d.tzOffset,
        latitude: d.lat,
        longitude: d.lon,
        place_name: d.placeName ?? null,
        is_self: true,
        visibility: 'private',
        imported_positions: null,
        house_system: d.houseSystem ?? 'placidus',
      };
      const { data: existing } = await supabase
        .from('birth_charts')
        .select('id')
        .eq('user_id', userId)
        .eq('is_self', true)
        .maybeSingle();
      const { data: inserted, error } = existing
        ? await supabase
            .from('birth_charts')
            .update({ ...payload, updated_at: new Date().toISOString() })
            .eq('id', existing.id)
            .select()
            .single()
        : await supabase.from('birth_charts').insert(payload).select().single();
      if (error) throw error;
      const chart = computeChart({
        name: d.name || 'self',
        year: d.year, month: d.month, day: d.day,
        hour: d.hour, minute: d.minute,
        tzOffset: d.tzOffset, lat: d.lat, lon: d.lon, placeName: d.placeName,
        houseSystem: d.houseSystem ?? 'placidus',
      });
      await supabase.from('astrology_chart_cache').upsert([{
        birth_chart_id: inserted.id,
        computed_chart: chart as never,
        computed_at: new Date().toISOString(),
      }]);
    } else {
      const payload = {
        user_id: userId,
        label: 'self',
        is_self: true,
        visibility: 'private' as const,
        imported_positions: preview.positions,
        house_system: preview.houseSystem ?? 'whole',
      };
      const { data: existing } = await supabase
        .from('birth_charts')
        .select('id')
        .eq('user_id', userId)
        .eq('is_self', true)
        .maybeSingle();
      const { data: inserted, error } = existing
        ? await supabase
            .from('birth_charts')
            .update({ ...payload, updated_at: new Date().toISOString() })
            .eq('id', existing.id)
            .select()
            .single()
        : await supabase.from('birth_charts').insert(payload).select().single();
      if (error) throw error;
      const chart = chartFromPositions(preview.positions, 'self', preview.houseSystem ?? 'whole');
      await supabase.from('astrology_chart_cache').upsert([{
        birth_chart_id: inserted.id,
        computed_chart: chart as never,
        computed_at: new Date().toISOString(),
      }]);
    }

    clearAstrologyPreview();
    return true;
  } catch (err) {
    console.warn('[astrology] preview migration failed', err);
    // Don't block auth flow on migration failure; leave the preview in place
    // so the user can retry from /astrology after sign-in.
    return false;
  }
}